import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import {
  LayoutDashboard,
  Globe,
  Server,
  Network,
  Boxes,
  Shield,
  List,
  Users,
  Settings,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Monitor,
  Activity,
  NeeveEndpoint,
} from '@/utils/icons';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores/uiStore';
import { logout } from '@/services/api';
import { useFeatureVisibility } from '@/hooks/useFeatureVisibility';

// Each nav item carries a `featureId` matching the canonical feature catalog
// (frontend/src/config/features.ts when Week 2 lands). The sidebar reads the
// feature visibility hook to decide which items to render — for Week 1 this
// metadata is informational and every item is shown unconditionally.
const navSections = [
  {
    label: 'Analytics',
    items: [
      { to: '/analytics/overview', icon: Activity, label: 'Overview', featureId: 'route.analytics.overview' },
      { to: '/analytics/active-users', icon: Users, label: 'Active Users', featureId: 'route.analytics.active-users' },
      { to: '/analytics/active-endpoints', icon: NeeveEndpoint, label: 'Active Endpoints', featureId: 'route.analytics.active-endpoints' },
      { to: '/analytics/security-policies', icon: Shield, label: 'Security Policies', featureId: 'route.analytics.security-policies' },
    ],
  },
  {
    label: 'Secure Edge',
    items: [
      { to: '/', icon: LayoutDashboard, label: 'Dashboard', featureId: 'route.dashboard' },
      { to: '/triage', icon: AlertTriangle, label: 'Triage', featureId: 'route.triage' },
      { to: '/portfolios', icon: Globe, label: 'Portfolios', featureId: 'route.portfolios' },
      { to: '/fleet', icon: Server, label: 'Inventory', featureId: 'route.fleet' },
      { to: '/nodes', icon: List, label: 'Nodes', featureId: 'route.nodes' },
      { to: '/networks', icon: Network, label: 'Networks', featureId: 'route.networks' },
      { to: '/services', icon: Boxes, label: 'Services', featureId: 'route.services' },
      { to: '/security', icon: Shield, label: 'Security', featureId: 'route.security' },
      { to: '/users', icon: Users, label: 'Users', featureId: 'route.users' },
    ],
  },
  {
    label: 'Remote Access',
    items: [],
  },
];

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar, sidebarOpen, setSidebarOpen, setSession, session } = useUIStore();
  const { isVisible } = useFeatureVisibility();
  const navigate = useNavigate();
  const location = useLocation();

  // Close mobile sidebar on navigation
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname, setSidebarOpen]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // Server-side session cleanup may fail, but we still clear client state
    }
    setSession({ authenticated: false, tenant: null });
    navigate('/login');
  };

  return (
    <>
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="dls-fixed dls-inset-0 dls-z-40 dls-bg-black/50 lg:dls-hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside
        className={cn(
          'dls-fixed dls-left-0 dls-top-0 dls-z-50 dls-h-screen dls-bg-white dls-border-r dls-border-border dls-transition-all dls-duration-300 dls-flex dls-flex-col',
          // Mobile: hidden by default, slide in when open
          sidebarOpen ? 'dls-translate-x-0' : '-dls-translate-x-full',
          'lg:dls-translate-x-0',
          sidebarCollapsed ? 'dls-w-60 lg:dls-w-16' : 'dls-w-60'
        )}
      >
      {/* Logo + collapse toggle */}
      <div
        className={cn(
          'dls-relative dls-flex dls-items-center dls-h-16 dls-border-b dls-border-border',
          sidebarCollapsed ? 'dls-justify-center dls-px-2' : 'dls-justify-between dls-px-4'
        )}
      >
        {sidebarCollapsed ? (
          <>
            <img
              src="/assets/logos/Neeve-Logomark_flush.svg"
              alt="Neeve"
              className="dls-h-8 dls-w-8"
            />
            <button
              onClick={toggleSidebar}
              className="dls-absolute dls-right-1 dls-text-muted-foreground hover:dls-text-foreground dls-transition-colors dls-hidden lg:dls-block"
              title="Expand sidebar"
            >
              <ChevronRight className="dls-h-4 dls-w-4" />
            </button>
          </>
        ) : (
          <>
            <div className="dls-flex dls-flex-col">
              <div className="dls-flex dls-items-center dls-gap-2">
                <img
                  src="/assets/logos/Neeve-Logo-Lockup_flush.svg"
                  alt="Neeve"
                  className="dls-h-8 dls-w-auto"
                />
                {__APP_VERSION__.startsWith('0.') && (
                  <span className="dls-bg-neeve-orange dls-text-white dls-rounded-full dls-px-2 dls-py-0.5 dls-text-[10px] dls-font-semibold dls-leading-none">
                    Beta
                  </span>
                )}
              </div>
              <div className="dls-flex dls-items-center dls-gap-2 dls-mt-1">
                <p className="dls-text-xs dls-font-medium dls-text-neeve-primary dls-tracking-wide dls-uppercase">Insights</p>
                {session?.isDemo && (
                  <p className="dls-text-[10px] dls-font-semibold dls-text-status-warning dls-tracking-wider dls-uppercase">
                    Demo Mode
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={toggleSidebar}
              className="dls-text-muted-foreground hover:dls-text-foreground dls-transition-colors dls-hidden lg:dls-block"
              title="Collapse sidebar"
            >
              <ChevronLeft className="dls-h-4 dls-w-4" />
            </button>
          </>
        )}
      </div>

      {/* Navigation */}
      <nav className="dls-flex-1 dls-flex dls-flex-col dls-p-2 dls-pt-2 dls-overflow-y-auto">
        {navSections.map((section) => {
          // Filter items by feature visibility — hide a section header entirely
          // when no items would render (either the section has no items at all,
          // or all items are flag-hidden for the current user). Empty sections
          // are placeholders for future content (e.g., Remote Access portal
          // links) and should not render their header until they have items.
          const visibleItems = section.items.filter((item) => isVisible(item.featureId));
          if (visibleItems.length === 0) {
            return null;
          }
          return (
            <div key={section.label}>
              {!sidebarCollapsed && (
                <span className="dls-px-3 dls-pt-4 dls-pb-1.5 dls-text-[11px] dls-font-semibold dls-uppercase dls-tracking-wider dls-text-muted-foreground/50 dls-block">
                  {section.label}
                </span>
              )}
              {visibleItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  title={sidebarCollapsed ? item.label : undefined}
                  className={({ isActive }) =>
                    cn(
                      'dls-flex dls-items-center dls-gap-3 dls-rounded-lg dls-py-2.5 dls-text-sm dls-font-medium dls-transition-colors',
                      sidebarCollapsed ? 'dls-justify-center dls-px-2' : 'dls-px-3',
                      isActive
                        ? 'dls-bg-neeve-primary/10 dls-text-neeve-primary'
                        : 'dls-text-muted-foreground hover:dls-bg-muted hover:dls-text-foreground'
                    )
                  }
                >
                  <item.icon className="dls-h-5 dls-w-5 dls-flex-shrink-0" />
                  {!sidebarCollapsed && <span>{item.label}</span>}
                </NavLink>
              ))}
            </div>
          );
        })}
        {/* Settings + Dev Console - standalone at bottom of nav */}
        <div className="dls-mt-auto">
          <NavLink
            to="/settings"
            title={sidebarCollapsed ? 'Settings' : undefined}
            className={({ isActive }) =>
              cn(
                'dls-flex dls-items-center dls-gap-3 dls-rounded-lg dls-py-2.5 dls-text-sm dls-font-medium dls-transition-colors',
                sidebarCollapsed ? 'dls-justify-center dls-px-2' : 'dls-px-3',
                isActive
                  ? 'dls-bg-neeve-primary/10 dls-text-neeve-primary'
                  : 'dls-text-muted-foreground hover:dls-bg-muted hover:dls-text-foreground'
              )
            }
          >
            <Settings className="dls-h-5 dls-w-5 dls-flex-shrink-0" />
            {!sidebarCollapsed && <span>Settings</span>}
          </NavLink>
          {session?.role === 'dev' && (
            <NavLink
              to="/dev-console"
              title={sidebarCollapsed ? 'Dev Console' : undefined}
              className={({ isActive }) =>
                cn(
                  'dls-flex dls-items-center dls-gap-3 dls-rounded-lg dls-py-2.5 dls-text-sm dls-font-medium dls-transition-colors',
                  sidebarCollapsed ? 'dls-justify-center dls-px-2' : 'dls-px-3',
                  isActive
                    ? 'dls-bg-neeve-primary/10 dls-text-neeve-primary'
                    : 'dls-text-muted-foreground hover:dls-bg-muted hover:dls-text-foreground'
                )
              }
            >
              <Monitor className="dls-h-5 dls-w-5 dls-flex-shrink-0" />
              {!sidebarCollapsed && <span>Dev Console</span>}
            </NavLink>
          )}
        </div>
      </nav>

      {/* Bottom: sign out */}
      <div className="dls-px-2 dls-py-3 dls-border-t dls-border-border">
        <button
          onClick={handleLogout}
          title={sidebarCollapsed ? 'Sign out' : undefined}
          className={cn(
            'dls-w-full dls-flex dls-items-center dls-py-2.5 dls-text-sm dls-font-medium dls-text-muted-foreground hover:dls-text-foreground hover:dls-bg-muted dls-rounded-lg dls-transition-colors',
            sidebarCollapsed ? 'dls-justify-center dls-px-2' : 'dls-gap-3 dls-px-3'
          )}
        >
          <LogOut className="dls-h-5 dls-w-5 dls-flex-shrink-0" />
          {!sidebarCollapsed && <span>Sign out</span>}
        </button>
      </div>
    </aside>
    </>
  );
}
